import { toast } from "@risalabs_frontend_org/oasis-ui-kit";
import { cloneDeep } from "lodash";
import { Dispatch, SetStateAction } from "react";
import { BadgeWithIconProps } from "../../../../../data-model/badgeWithIconProps";
import {
  OrderAssignmentConfigService,
  OrderAssignmentConfiguration,
} from "../../../../../../shared/api/services/orderAssignmentConfigService";
import { HUMAN_MEDICAL_ORDER_STATUSES } from "./medicalOrderStatusConstants";

/**
 * Helper function to create a deep copy of an object
 */
export const deepClone = <T>(obj: T): T => {
  return cloneDeep(obj);
};

/**
 * Helper function to check if two configurations are different
 */
export const hasConfigurationChanged = (
  original: OrderAssignmentConfiguration,
  current: OrderAssignmentConfiguration,
): boolean => {
  return JSON.stringify(original) !== JSON.stringify(current);
};

/**
 * Filters provider assignment rows by provider name for the Auto Assignment table search.
 * Whitespace-only `searchTerm` returns `configurations` unchanged (same reference).
 *
 * @param configurations - Full list loaded for the selected facility
 * @param searchTerm - Raw string from the table search field
 * @returns Subset of configurations whose `provider_name` contains the term (case-insensitive)
 */
export const filterOrderAssignmentConfigurationsByProviderSearch = (
  configurations: OrderAssignmentConfiguration[],
  searchTerm: string,
): OrderAssignmentConfiguration[] => {
  if (!searchTerm.trim()) {
    return configurations;
  }
  const lowerSearchTerm = searchTerm.toLowerCase();
  return configurations.filter((config) =>
    config.provider_name?.toLowerCase().includes(lowerSearchTerm),
  );
};

/**
 * Convert facility options to badge format
 */
export const convertFacilityOptionsToBadges = (
  facilityOptions: Array<{ value: string; label: string }>,
): BadgeWithIconProps[] => {
  return facilityOptions.map((option) => ({
    id: option.value,
    text: option.label,
    textColor: "#374151",
    bgColor: "#F3F4F6",
    borderColor: "#374151",
  }));
};

/**
 * Get the selected facility badge, or a placeholder badge if none is selected
 */
export const getSelectedFacilityBadge = (
  selectedFacilityId: string,
  facilityBadgeOptions: BadgeWithIconProps[],
  placeholderText: string,
): BadgeWithIconProps | null => {
  if (!selectedFacilityId) {
    return {
      id: "",
      text: placeholderText,
      textColor: "#6B7280",
      bgColor: "#F9FAFB",
      borderColor: "#D1D5DB",
    };
  }
  return (
    facilityBadgeOptions.find((badge) => badge.id === selectedFacilityId) ||
    null
  );
};

/**
 * Compares two payer patterns in a case-insensitive, trimmed manner.
 * Used for matching payer patterns that may have different casing or whitespace.
 *
 * @param {string} selected - The selected payer pattern
 * @param {string} pattern - The pattern to compare against
 * @returns {boolean} True if the patterns match (case-insensitive, trimmed)
 */
export const comparePayerPatterns = (
  selected: string,
  pattern: string,
): boolean => {
  return selected.toLowerCase().trim() === pattern.toLowerCase().trim();
};

/**
 * Checks if all options in the provided list are selected.
 * Uses a custom comparison function to determine if a selected item matches an option.
 *
 * @param {TSelected[]} selected - Array of selected items
 * @param {TOption[]} allOptions - Array of all available options to check against
 * @param {function} compareFn - Function that compares a selected item with an option, returns true if they match
 * @returns {boolean} True if all options are selected, false otherwise
 */
export const areAllSelected = <TSelected, TOption>(
  selected: TSelected[],
  allOptions: TOption[],
  compareFn: (selected: TSelected, option: TOption) => boolean,
): boolean => {
  return (
    allOptions.length > 0 &&
    allOptions.every((option) => selected.some((sel) => compareFn(sel, option)))
  );
};

/**
 * Converts selected items to ["any"] if all options are selected, otherwise returns the selected items.
 * Used for saving configurations where "any" represents all items being selected.
 *
 * @param {TSelected[]} selected - Array of selected items
 * @param {TOption[]} allOptions - Array of all available options to check against
 * @param {function} compareFn - Function that compares a selected item with an option, returns true if they match
 * @returns {string[]} Returns ["any"] if all options are selected, otherwise returns the selected array
 */
export const convertToAnyIfAllSelected = <TSelected, TOption>(
  selected: TSelected[],
  allOptions: TOption[],
  compareFn: (selected: TSelected, option: TOption) => boolean,
): string[] => {
  const allSelected = areAllSelected(selected, allOptions, compareFn);
  return allSelected ? ["any"] : (selected as string[]);
};

/**
 * Creates a toggle handler function for managing array state.
 * Toggles an item in the array: removes it if present, adds it if not present.
 *
 * @param {React.Dispatch<React.SetStateAction<T[]>>} setter - React state setter function
 * @returns {(item: T) => void} Toggle handler function that takes an item and toggles its presence in the array
 */
export const createToggleHandler =
  <T>(setter: Dispatch<SetStateAction<T[]>>) =>
  (item: T) => {
    setter((prev) =>
      prev.includes(item) ? prev.filter((p) => p !== item) : [...prev, item],
    );
  };

/**
 * Checks if the current time is between 9:30 AM and 1:00 PM (blocked time window).
 * @returns {boolean} True if current time is within the blocked window (9:30 AM - 1:00 PM), false otherwise
 */
export const isWithinBlockedTimeWindow = (): boolean => {
  const now = new Date();
  const currentHour = now.getHours();
  const currentMinute = now.getMinutes();
  const currentTimeInMinutes = currentHour * 60 + currentMinute;

  // 9:30 AM = 9 * 60 + 30 = 570 minutes
  const blockedStartMinutes = 9 * 60 + 30; // 9:30 AM
  // 1:00 PM = 13 * 60 = 780 minutes
  const blockedEndMinutes = 13 * 60; // 1:00 PM

  return (
    currentTimeInMinutes >= blockedStartMinutes &&
    currentTimeInMinutes < blockedEndMinutes
  );
};

/**
 * Handle organization change - updates facility_id and resets all values to defaults
 * @param config - The current configuration object
 * @param selectedOrgId - The ID of the organization to change to
 * @param onOrgChange - Optional callback to refresh configurations after org change
 * @param handleCancel - Callback to close the modal
 * @returns Promise that resolves when the organization change is complete
 */
export const handleOrganizationChange = async (
  config: OrderAssignmentConfiguration | null,
  selectedOrgId: string,
  onOrgChange?: () => void,
  handleCancel?: () => void,
): Promise<void> => {
  if (!config?.id || !selectedOrgId) {
    toast.error("Please select an organization");
    return;
  }

  if (selectedOrgId === config.facility_id) {
    toast.error("Provider is already assigned to this organization");
    return;
  }

  try {
    // Get all payer patterns and auth statuses for the new facility to set "Any"
    // For now, we'll set them to ["any"] which represents all selected
    const updates: Partial<OrderAssignmentConfiguration> = {
      facility_id: selectedOrgId,
      capacity: 50,
      if_multi_regimen: false,
      working_today: false,
      medical_order_status: HUMAN_MEDICAL_ORDER_STATUSES,
      auth_status: ["any"],
      payer_patterns: ["any"],
    };

    await OrderAssignmentConfigService.updateConfiguration(config.id, updates);

    toast.success("Provider successfully moved to the new organization");

    // Call the callback to refresh configurations in the parent
    if (onOrgChange) {
      onOrgChange();
    }

    // Close the modal
    if (handleCancel) {
      handleCancel();
    }
  } catch (error) {
    const errorMessage =
      error instanceof Error ? error.message : "Failed to change organization";
    toast.error(`Error: ${errorMessage}`);
    throw error;
  }
};
